import { FC, ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getTenant } from '../../store/activeTenant.slice';
import { isBasicPlanValue } from '../../app-constants/subscription-plan.enum';
import { getProfileData } from '../../store/profile.slice';
import { IoLockClosed, IoClose } from 'react-icons/io5';
import {
  PreviewContainer,
  ContentWrapper,
  Overlay,
  UpgradeCard,
  CloseButton,
  Icon,
  Title,
  Description,
  UpgradeButton,
  OKButton,
} from './styles';

interface FeaturePreviewProps {
  children: ReactNode;
  title?: string;
  description?: string;
  isBlocked: boolean;
}

/**
 * FeaturePreview Component
 *
 * Shows content in a disabled/preview state when feature is blocked
 * Displays an overlay with upgrade prompt while keeping content visible but non-interactive
 *
 * Usage:
 * const { isBlocked } = useFeatureGate('can_use_workflows');
 * <FeaturePreview isBlocked={isBlocked} title="..." description="...">
 *   <YourContent />
 * </FeaturePreview>
 */
export const FeaturePreview: FC<FeaturePreviewProps> = ({
  children,
  title = 'هذه الميزة غير متاحة في خطتك الحالية',
  description = 'قم بالترقية للوصول إلى هذه الميزة الكاملة',
  isBlocked,
}) => {
  const tenant = useSelector(getTenant);
  const navigate = useNavigate();
  const profile = useSelector(getProfileData);
  const isOwner = tenant?.is_owner === true;

  const planValue = Number(profile?.subscription_info?.plan_value);
  const isBasicPlan = isBasicPlanValue(planValue);
  const shouldShowUpgradeButton = isOwner && isBasicPlan;

  const handleUpgrade = () => {
    navigate('/plans');
  };

  const handleClose = () => {
    navigate(-1);
  };

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  if (!isBlocked) {
    return <>{children}</>;
  }

  // When blocked, show children with empty states (data queries disabled) and overlay on top
  return (
    <PreviewContainer>
      <ContentWrapper $isBlocked={isBlocked}>
        {children}
      </ContentWrapper>

      <Overlay onClick={handleOverlayClick}>
        <UpgradeCard>
          <CloseButton onClick={handleClose}>
            <IoClose size={24} />
          </CloseButton>

          <Icon>
            <IoLockClosed size={36} />
          </Icon>

          <Title>{title}</Title>
          <Description>{description}</Description>

          {shouldShowUpgradeButton ? (
            <UpgradeButton onClick={handleUpgrade}>
              عرض الباقات
            </UpgradeButton>
          ) : !isOwner ? (
            <Description style={{ fontSize: '14px', margin: '0' }}>
              يرجى التواصل مع مالك الحساب لترقية الخطة
            </Description>
          ) : null}

          {!shouldShowUpgradeButton && isOwner && (
            <OKButton onClick={handleClose}>حسناً</OKButton>
          )}
        </UpgradeCard>
      </Overlay>
    </PreviewContainer>
  );
};
